import { Customer } from "@jugl-web/rest-api/customer";
import { TaskDefaultStatus, TaskPriority } from "@jugl-web/rest-api/tasks";
import { TaskTemplateFolder } from "@jugl-web/rest-api/tasks-templates";
import { HookOutOfContextError } from "@jugl-web/utils";
import isEqual from "lodash/isEqual";
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { TaskChecklistItem } from "../components/TaskChecklist";

export interface TaskFormState {
  title: string;
  description: string;
  dueDate: Date | null;
  assigneeIds: string[];
  labelId: string | null;
  statusId: TaskDefaultStatus | string;
  priority: TaskPriority;
  customer: Customer | null;
  attachments: { id: string; file: File }[];
  customFields: Record<string, string>;
  checklistItems: TaskChecklistItem[];
  saveToTemplateFolder: TaskTemplateFolder | null;
  allowAssigneesEdit: boolean;
  completeChecklistInOrder: boolean;
  isPrivate: boolean;
}

export type UpdateTaskFormStateFn<
  TState extends Partial<TaskFormState> = Partial<TaskFormState>
> = <TField extends keyof TState, TValue extends TState[TField]>(
  field: TField,
  value: TValue
) => void;

export type ResetTaskFormStateFn<
  TState extends Partial<TaskFormState> = Partial<TaskFormState>
> = (newState?: TState) => void;

export const INITIAL_FORM_STATE: TaskFormState = {
  title: "",
  description: "",
  dueDate: null,
  assigneeIds: [],
  labelId: null,
  statusId: TaskDefaultStatus.notStarted,
  priority: TaskPriority.none,
  customer: null,
  attachments: [],
  customFields: {},
  checklistItems: [],
  saveToTemplateFolder: null,
  allowAssigneesEdit: false,
  completeChecklistInOrder: false,
  isPrivate: false,
};

export const useTaskFormState = <TState extends Partial<TaskFormState>>(
  initialState?: TState
) => {
  const [internalInitialState, setInternalInitialState] = useState(() => ({
    ...INITIAL_FORM_STATE,
    ...initialState,
  }));

  const [formState, setFormState] =
    useState<TaskFormState>(internalInitialState);

  const isDirty = useCallback(
    () => !isEqual(formState, internalInitialState),
    [formState, internalInitialState]
  );

  const resetFormState = useCallback<ResetTaskFormStateFn<TState>>(
    (newState) => {
      const newInitialState = { ...INITIAL_FORM_STATE, ...newState };
      setInternalInitialState(newInitialState);
      setFormState(newInitialState);
    },
    []
  );

  const updateFormState = useCallback<UpdateTaskFormStateFn<TState>>(
    (field, value) =>
      setFormState((prevFormState) => ({ ...prevFormState, [field]: value })),
    []
  );

  const updateChecklistItems = useCallback(
    (
      value:
        | TaskChecklistItem[]
        | ((previousValue: TaskChecklistItem[]) => TaskChecklistItem[])
    ) => {
      updateFormState(
        "checklistItems",
        typeof value === "function" ? value(formState.checklistItems) : value
      );
    },
    [formState.checklistItems, updateFormState]
  );

  const updateCustomField = useCallback(
    (customFieldId: string, value: string) => {
      updateFormState("customFields", {
        ...formState.customFields,
        [customFieldId]: value,
      });
    },
    [formState.customFields, updateFormState]
  );

  const formContext = {
    formState,
    isDirty,
    resetFormState,
    updateFormState,
    updateChecklistItems,
    updateCustomField,
  };

  return { ...formContext, formContext };
};

// #region Context-related stuff
type UseTaskFormStateContext = ReturnType<
  typeof useTaskFormState
>["formContext"];

const TaskFormStateContext = createContext<UseTaskFormStateContext | null>(
  null
);

interface TaskFormStateProviderProps {
  children: ReactNode;
  context: UseTaskFormStateContext;
}

export const TaskFormStateProvider: FC<TaskFormStateProviderProps> = ({
  children,
  context,
}) => (
  <TaskFormStateContext.Provider value={context}>
    {children}
  </TaskFormStateContext.Provider>
);

export const useTaskFormStateContext = () => {
  const context = useContext(TaskFormStateContext);

  if (!context) {
    throw new HookOutOfContextError(
      "useTaskFormStateContext",
      "TaskFormStateProvider"
    );
  }

  return context;
};
// #endregion
